import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import sh from "bundles/common/utils/sh";

const inputSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
    borderRadius: "12px",
    fontSize: 14,
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(150 30% 12%)",
    backgroundColor: "hsl(140 10% 95%)",
    "& fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&:hover fieldset": { borderColor: "hsl(140 8% 88%)" },
    "&.Mui-focused fieldset": { borderColor: "hsl(150 38% 17%)", borderWidth: 2 },
    "&.Mui-focused": { backgroundColor: "#fff" },
  },
};

const labelSx = {
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 12,
  color: "hsl(150 10% 46%)",
  mb: 0.75,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

const AddCreditsDialog = ({ open, onClose, onSubmit, loading, companyId }) => {
  const [form, setForm] = useState({ credit_type: "licence", count: 1 });

  const [companySearch, setCompanySearch] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [searchingCompanies, setSearchingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const isCreateMode = !companyId;

  const handleClose = () => {
    setForm({ credit_type: "licence", count: 1 });
    setCompanySearch("");
    setCompanyResults([]);
    setSelectedCompany(null);
    onClose();
  };

  const handleSubmit = () => {
    const payload = { ...form };
    if (isCreateMode) payload.company_id = selectedCompany?.id;
    onSubmit(payload);
  };

  const isSubmitDisabled = loading || (isCreateMode && !selectedCompany);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{ sx: { borderRadius: "16px", minWidth: 420, maxWidth: 480 } }}
    >
      <DialogTitle sx={{
        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
        fontWeight: 700,
        fontSize: 18,
        color: "hsl(150 30% 12%)",
        pt: 3,
        px: 3,
        pb: 1
      }}>
        Add Credits
      </DialogTitle>
      <DialogContent sx={{ px: 3, pb: 1, pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
        {isCreateMode && (
          <Box>
            <Typography sx={labelSx}>Company</Typography>
            {selectedCompany ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1,
                  borderRadius: "12px",
                  border: "1px solid hsl(140 8% 88%)",
                  backgroundColor: "hsl(140 10% 95%)",
                }}
              >
                <Typography sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" }}>
                  {selectedCompany.name}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  onClick={() => { setSelectedCompany(null); setCompanySearch(""); setCompanyResults([]); }}
                  sx={{ background: "none", border: "none", cursor: "pointer", color: "hsl(150 10% 46%)", fontSize: 18, lineHeight: 1, p: 0 }}
                >
                  ×
                </Box>
              </Box>
            ) : (
              <>
                <TextField
                  placeholder="Search by company name..."
                  value={companySearch}
                  onChange={(e) => {
                    setCompanySearch(e.target.value);
                    const q = e.target.value.trim();
                    if (q.length < 2) { setCompanyResults([]); return; }
                    setSearchingCompanies(true);
                    sh.get("/internal/search/companies", { params: { q } })
                      .then((res) => {
                        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
                        setCompanyResults(data);
                      })
                      .catch(() => setCompanyResults([]))
                      .finally(() => setSearchingCompanies(false));
                  }}
                  fullWidth
                  size="small"
                  sx={inputSx}
                />
                {searchingCompanies && (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
                    <CircularProgress size={20} sx={{ color: "hsl(150 38% 17%)" }} />
                  </Box>
                )}
                {!searchingCompanies && companySearch.trim().length >= 2 && companyResults.length === 0 && (
                  <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", textAlign: "center", py: 1.5, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>No companies found</Typography>
                )}
                {companyResults.length > 0 && (
                  <List disablePadding sx={{ mt: 0.5, maxHeight: 160, overflowY: "auto" }}>
                    {companyResults.map((company) => (
                      <ListItemButton
                        key={company.id}
                        onClick={() => { setSelectedCompany(company); setCompanySearch(""); setCompanyResults([]); }}
                        disableRipple
                        sx={{ borderRadius: "8px", mb: 0.25, "&:hover": { backgroundColor: "hsl(140 10% 90%)" } }}
                      >
                        <ListItemText
                          primary={company.name}
                          primaryTypographyProps={{ sx: { fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150 30% 12%)" } }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
              </>
            )}
          </Box>
        )}
        <Box>
          <Typography sx={labelSx}>Credit Type</Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={form.credit_type}
            onChange={(e) => setForm({ ...form, credit_type: e.target.value, count: 1 })}
            sx={inputSx}
          >
            <MenuItem value="licence" sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              Licence
            </MenuItem>
            <MenuItem value="cv_access" sx={{ fontSize: 14, fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
              CV Access
            </MenuItem>
          </TextField>
        </Box>

        {form.credit_type === "licence" && (
          <Box>
            <Typography sx={labelSx}>Count</Typography>
            <Box
              component="input"
              type="number"
              value={form.count}
              onChange={(e) => setForm({ ...form, count: parseInt(e.target.value) || 1 })}
              min="1"
              sx={{
                width: "100%",
                height: 40,
                borderRadius: "12px",
                border: "1px solid hsl(140 8% 88%)",
                backgroundColor: "hsl(140 10% 95%)",
                px: 1.5,
                py: 1,
                fontSize: 14,
                fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                color: "hsl(150 30% 12%)",
                outline: "none",
                boxSizing: "border-box",
                "&:focus": {
                  borderColor: "hsl(150 38% 17%)",
                  backgroundColor: "#fff"
                },
              }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            height: 40,
            borderRadius: "12px",
            textTransform: "none",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            fontSize: 14,
            color: "hsl(150 10% 46%)",
            "&:hover": { backgroundColor: "hsla(140, 8%, 90%, 0.5)" },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          sx={{
            height: 40,
            borderRadius: "12px",
            textTransform: "none",
            fontSize: 14,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            boxShadow: "none",
            "&:hover": { backgroundColor: "hsla(150, 38%, 17%, 0.9)", boxShadow: "none" },
            "&.Mui-disabled": {
              opacity: 0.5,
              backgroundColor: "hsl(150 38% 17%)",
              color: "hsl(80 97% 72%)"
            },
          }}
        >
          {loading ? "Adding..." : "Add Credits"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCreditsDialog;
